import { Controller } from "@hotwired/stimulus"

// Searchable page picker for `/insights`. Renders a static list of every
// page the user has connected (server-rendered so we don't need an API
// hit on open), filters client-side as they type, navigates to the
// selected page via Turbo.visit. ~50 pages is the realistic upper bound
// per the implementation plan — flat client-side filter is fine.
//
// Keyboard:
//   - Click button → toggle dropdown
//   - Arrow Down/Up while focus is in the search input → move highlight
//   - Enter → activate highlighted item
//   - Escape → close
//
// Outside-click closes the menu, mirroring the existing `dropdown_controller`.
export default class extends Controller {
  static targets = ["button", "menu", "search", "option", "empty"]
  static values = {
    selectedFbPageId: String,
  }

  connect() {
    console.log(`[insights-page-selector] connect: ${this.optionTargets.length} pages, selected=${this.selectedFbPageIdValue}`)
    this.highlightIndex = -1
    this._closeOnOutside = this._closeOnOutside.bind(this)
  }

  disconnect() {
    document.removeEventListener("click", this._closeOnOutside)
  }

  toggle(event) {
    event?.stopPropagation()
    const isHidden = this.menuTarget.classList.contains("hidden")
    if (isHidden) this.open()
    else this.close()
  }

  open() {
    this.menuTarget.classList.remove("hidden")
    document.addEventListener("click", this._closeOnOutside)
    // Focus search after a tick so the click event clears.
    setTimeout(() => {
      if (this.hasSearchTarget) this.searchTarget.focus()
    }, 10)
  }

  close() {
    this.menuTarget.classList.add("hidden")
    document.removeEventListener("click", this._closeOnOutside)
    this.highlightIndex = -1
    this._refreshHighlight()
  }

  filter(event) {
    const q = (event.target.value || "").trim().toLowerCase()
    let firstVisible = -1
    this.optionTargets.forEach((opt, i) => {
      const blob = (opt.dataset.search || "").toLowerCase()
      const visible = q === "" || blob.includes(q)
      opt.classList.toggle("hidden", !visible)
      if (visible && firstVisible === -1) firstVisible = i
    })
    this.highlightIndex = firstVisible
    this._refreshHighlight()
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", firstVisible !== -1)
  }

  keydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      this.buttonTarget?.focus()
      return
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      const visible = this._visibleOptions()
      if (visible.length === 0) return
      const cur = visible.indexOf(this.optionTargets[this.highlightIndex])
      const next = event.key === "ArrowDown"
        ? (cur + 1) % visible.length
        : (cur - 1 + visible.length) % visible.length
      this.highlightIndex = this.optionTargets.indexOf(visible[next])
      this._refreshHighlight()
      visible[next].scrollIntoView({ block: "nearest" })
      return
    }
    if (event.key === "Enter") {
      event.preventDefault()
      if (this.highlightIndex >= 0) {
        this.optionTargets[this.highlightIndex].click()
      }
    }
  }

  selectFromClick(event) {
    const url = event.currentTarget.dataset.url
    if (url) {
      this.close()
      window.Turbo ? window.Turbo.visit(url) : (window.location.href = url)
    }
  }

  // private

  _visibleOptions() {
    return this.optionTargets.filter((opt) => !opt.classList.contains("hidden"))
  }

  _refreshHighlight() {
    this.optionTargets.forEach((opt, i) => {
      opt.toggleAttribute("data-highlighted", i === this.highlightIndex)
    })
  }

  _closeOnOutside(event) {
    if (this.element.contains(event.target)) return
    this.close()
  }
}
